import type { IsoDate, IsoDateTime } from './common';
import type {
  AllocationId,
  InventoryId,
  LocationId,
  MovementId,
  OrderId,
  OrderItemId,
  ProductId,
  TenantId,
  UserId,
  WarehouseId,
} from './ids';

/** The three stock figures of Modül A. */
export interface StockLevels {
  /** fiziksel_stok: units physically in the warehouse */
  readonly physical: number;
  /** rezerve_stok: units promised to open orders (sum of reserved/picked allocations) */
  readonly reserved: number;
  /** kullanilabilir_stok: physical − reserved, counting only sellable, unexpired locations */
  readonly available: number;
}

/** One row of the stock_summary view (per tenant and product). */
export interface StockSummary extends StockLevels {
  readonly tenantId: TenantId;
  readonly productId: ProductId;
  readonly sku: string;
  /** In receiving/staging, not yet put away */
  readonly inbound: number;
  /** Quarantine, damaged, returns or expired */
  readonly blocked: number;
}

/**
 * Balance of one product/lot in one bin. Read-only for the application:
 * PostgreSQL derives it from inventory_movements and inventory_allocations.
 */
export interface InventoryRecord {
  readonly id: InventoryId;
  readonly tenantId: TenantId;
  readonly productId: ProductId;
  readonly warehouseId: WarehouseId;
  readonly locationId: LocationId;
  readonly lotNo: string | null;
  readonly expiryDate: IsoDate | null;
  readonly qtyOnHand: number;
  readonly qtyReserved: number;
  readonly qtyAvailable: number;
  /** FIFO key for products without expiry tracking. */
  readonly firstReceivedAt: IsoDateTime;
  readonly updatedAt: IsoDateTime;
}

export const MOVEMENT_TYPES = ['receive', 'transfer', 'adjust_in', 'adjust_out', 'ship', 'return_in', 'dispose'] as const;
export type MovementType = (typeof MOVEMENT_TYPES)[number];

/** 'ship' rows are booked by PostgreSQL when an allocation is consumed, never inserted directly. */
export type ManualMovementType = Exclude<MovementType, 'ship'>;

/** Append-only stock ledger row (stok hareketi). */
export interface InventoryMovement {
  readonly id: MovementId;
  readonly tenantId: TenantId;
  readonly productId: ProductId;
  readonly warehouseId: WarehouseId;
  readonly type: MovementType;
  readonly qty: number;
  /** null for inbound types (receive, adjust_in, return_in) */
  readonly fromLocationId: LocationId | null;
  /** null for outbound types (ship, adjust_out, dispose) */
  readonly toLocationId: LocationId | null;
  readonly lotNo: string | null;
  readonly expiryDate: IsoDate | null;
  readonly orderId: OrderId | null;
  readonly allocationId: AllocationId | null;
  readonly reason: string | null;
  /** ASN number, count sheet, etc. */
  readonly reference: string | null;
  readonly actorUserId: UserId | null;
  readonly createdAt: IsoDateTime;
}

export const ALLOCATION_STATUSES = ['reserved', 'picked', 'consumed', 'released'] as const;
/**
 * reserved → picked → consumed (packed: stock leaves the books)
 *         ↘ released (order cancelled / re-allocated)
 */
export type AllocationStatus = (typeof ALLOCATION_STATUSES)[number];

/** Hard reservation of a quantity in a specific bin/lot for an order line. */
export interface InventoryAllocation {
  readonly id: AllocationId;
  readonly tenantId: TenantId;
  readonly orderId: OrderId;
  readonly orderItemId: OrderItemId;
  readonly productId: ProductId;
  readonly inventoryId: InventoryId;
  readonly locationId: LocationId;
  readonly qty: number;
  readonly qtyPicked: number;
  readonly status: AllocationStatus;
  readonly createdAt: IsoDateTime;
  readonly pickedAt: IsoDateTime | null;
  readonly closedAt: IsoDateTime | null;
  readonly createdBy: UserId | null;
}

// --- son kullanma tarihi (SKT) -------------------------------------------------

/** Stock expiring within this many days is flagged for the warehouse and the seller. */
export const EXPIRY_WARNING_DAYS = 30;

/**
 * expired: past its date · short_life: still in date but under the product's minimum remaining
 * shelf life (cannot be shipped) · soon: within EXPIRY_WARNING_DAYS · ok.
 */
export type ExpiryState = 'expired' | 'short_life' | 'soon' | 'ok';

export const EXPIRY_STATE_LABELS: Readonly<Record<ExpiryState, string>> = {
  expired: 'SKT geçti',
  short_life: 'Raf ömrü yetersiz',
  soon: 'SKT yaklaşıyor',
  ok: 'SKT uygun',
};

/** Whole days from today to the expiry date (YYYY-MM-DD both); negative once expired. */
export function daysUntil(expiryDate: string, today: string): number {
  const at = (day: string) => Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
  return Math.round((at(expiryDate) - at(today)) / 86_400_000);
}

export function expiryState(expiryDate: string, today: string, minShelfLifeDays: number | null): ExpiryState {
  const days = daysUntil(expiryDate, today);
  if (days < 0) return 'expired';
  if (minShelfLifeDays !== null && days < minShelfLifeDays) return 'short_life';
  if (days <= EXPIRY_WARNING_DAYS) return 'soon';
  return 'ok';
}

/** One product's stock line on the Stok screen (seller: own products only). */
export interface StockLine {
  readonly tenantId: TenantId;
  readonly tenantCode: string;
  readonly productId: ProductId;
  readonly sku: string;
  readonly name: string;
  readonly physical: number;
  readonly reserved: number;
  readonly available: number;
  readonly inbound: number;
  readonly blocked: number;
  readonly trackLot: boolean;
  readonly trackExpiry: boolean;
  readonly minShelfLifeDays: number | null;
  /** Earliest expiry date still in date among the units on hand */
  readonly nextExpiry: string | null;
  /** Units within EXPIRY_WARNING_DAYS or under the minimum shelf life */
  readonly expiringQty: number;
  readonly expiredQty: number;
}

/** A lot of a product (FEFO order) and where its units are. */
export interface StockLot {
  readonly lotNo: string | null;
  readonly expiryDate: string | null;
  readonly warehouseCode: string;
  readonly physical: number;
  readonly reserved: number;
  readonly available: number;
  readonly firstReceivedAt: IsoDateTime;
  /** Bins and areas holding it: code for the warehouse, the kind of place for everyone */
  readonly places: readonly { readonly code: string; readonly type: string; readonly qty: number }[];
}
